import { useState, useEffect, useRef } from "react";
import { ImageOverlay, useMapEvents } from "react-leaflet";
import { GeoJSONData } from "../GeoJSONData";

interface ScaleDependentLayerProps {
  typename: string;
  workspace: string;
  style?: any;
  customIcon?: string;
  customIconSize?: number;
  zIndex?: number;
  useWmsPreview?: boolean;
  minZoomForFeatures?: number;
  cqlFilter?: string;
  wmsColor?: string; // Recolor the WMS preview image to this hex color
}

/**
 * Compute a CSS filter string that converts the GeoServer default orange
 * (#FF6B00, HSL 25°/100%/50%) to any target hex color.
 */
function computeCssFilter(targetHex: string): string {
  // Parse target hex to RGB
  const r = parseInt(targetHex.slice(1, 3), 16) / 255;
  const g = parseInt(targetHex.slice(3, 5), 16) / 255;
  const b = parseInt(targetHex.slice(5, 7), 16) / 255;

  // RGB → HSL
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
    else if (max === g) h = ((b - r) / d + 2) * 60;
    else h = ((r - g) / d + 4) * 60;
  }

  const sourceHue = 25; // #FF6B00 hue
  const sourceL = 50; // #FF6B00 lightness %

  let hueRotation = Math.round(h - sourceHue);
  if (hueRotation < 0) hueRotation += 360;

  const brightness = parseFloat(((l * 100) / sourceL).toFixed(2));
  const saturate = parseFloat(((s * 100) / 100).toFixed(2)); // source is 100% saturated

  return `hue-rotate(${hueRotation}deg) brightness(${brightness}) saturate(${saturate})`;
}

const GEOSERVER_URL = "https://geoserver-atlas.isitics.com/geoserver";

export function ScaleDependentLayer({
  typename,
  workspace,
  style,
  customIcon,
  customIconSize,
  zIndex,
  useWmsPreview = false,
  minZoomForFeatures = 12,
  cqlFilter,
  wmsColor,
}: ScaleDependentLayerProps) {
  const [zoom, setZoom] = useState<number>(7);
  const [wmsUrl, setWmsUrl] = useState<string>("");
  const overlayRef = useRef<any>(null);

  const map = useMapEvents({
    zoomend: (e) => setZoom(e.target.getZoom()),
    moveend: () => {
      if (zoom < minZoomForFeatures && useWmsPreview) updateWmsUrl();
    },
  });

  const updateWmsUrl = () => {
    const bounds = map.getBounds();
    const size = map.getSize();
    const bbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;

    let url =
      `${GEOSERVER_URL}/${workspace}/wms?` +
      `service=WMS&version=1.1.0&request=GetMap&` +
      `layers=${workspace}:${typename}&styles=&` +
      `bbox=${bbox}&width=${size.x}&height=${size.y}&` +
      `srs=EPSG:4326&format=image/png&transparent=true`;

    if (cqlFilter) url += `&CQL_FILTER=${encodeURIComponent(cqlFilter)}`;

    setWmsUrl(url);
  };

  useEffect(() => {
    if (zoom < minZoomForFeatures && useWmsPreview) updateWmsUrl();
  }, [zoom, typename, workspace, minZoomForFeatures, useWmsPreview, cqlFilter]);

  // Apply CSS color filter to the <img> element whenever the overlay or color changes
  useEffect(() => {
    if (!wmsColor || !overlayRef.current) return;
    const img: HTMLImageElement | undefined = overlayRef.current._image;
    if (!img) return;
    const filter = computeCssFilter(wmsColor);
    img.style.filter = filter;
    // Re-apply after the image loads (src may change on pan/zoom)
    const onLoad = () => {
      img.style.filter = filter;
    };
    img.addEventListener("load", onLoad);
    return () => img.removeEventListener("load", onLoad);
  }, [wmsUrl, wmsColor]);

  if (!useWmsPreview) {
    return (
      <GeoJSONData
        typename={typename}
        workspace={workspace}
        style={style}
        customIcon={customIcon}
        customIconSize={customIconSize}
        zIndex={zIndex}
        cqlFilter={cqlFilter}
      />
    );
  }

  const showFeatures = zoom >= minZoomForFeatures;

  if (!showFeatures && wmsUrl) {
    return (
      <ImageOverlay
        ref={overlayRef}
        url={wmsUrl}
        bounds={map.getBounds()}
        opacity={0.8}
        zIndex={zIndex}
      />
    );
  }

  return (
    <GeoJSONData
      typename={typename}
      workspace={workspace}
      style={style}
      customIcon={customIcon}
      customIconSize={customIconSize}
      zIndex={zIndex}
      cqlFilter={cqlFilter}
    />
  );
}
